/**
 * SN-8441 / SN-8442 — reading what the server said when a Team Budget payment step
 * write did not go ahead. `req` throws the 4xx JSON body itself (never an Error), so
 * these read its fields directly.
 */

interface ApiErrorLike {
  status?: unknown
  statusCode?: unknown
  error_code?: unknown
  message?: unknown
  invalid_fields?: Record<string, unknown> | null
  errors?: Record<string, unknown> | null
}

const asApiError = (error: unknown): ApiErrorLike | null =>
  error !== null && typeof error === 'object' ? (error as ApiErrorLike) : null

export const statusOf = (error: unknown): number | null => {
  const apiError = asApiError(error)
  const status = Number(apiError?.status ?? apiError?.statusCode)

  return Number.isFinite(status) && status > 0 ? status : null
}

/**
 * The server's message for one field of a 422 — Laravel's `errors`, or the
 * `invalid_fields` the request layer normalizes them into — or null.
 */
export const readSessionFieldError = (
  error: unknown,
  field: string
): string | null => {
  const apiError = asApiError(error)

  if (apiError === null) {
    return null
  }

  const fromInvalid = apiError.invalid_fields?.[field]
  if (typeof fromInvalid === 'string' && fromInvalid !== '') {
    return fromInvalid
  }
  if (Array.isArray(fromInvalid) && typeof fromInvalid[0] === 'string') {
    return fromInvalid[0]
  }

  const fromErrors = apiError.errors?.[field]
  if (Array.isArray(fromErrors) && typeof fromErrors[0] === 'string') {
    return fromErrors[0]
  }

  return typeof fromErrors === 'string' && fromErrors !== '' ? fromErrors : null
}

/**
 * What the step does next after the server refused a payment-step write:
 * - `retry`    — another change is being made, or the provider did not answer: try again;
 * - `reprice`  — the amount due changed: choose again to see the new amount;
 * - `locked`   — a payment was taken (or is being taken): never pay again;
 * - `unpaid`   — confirmation without the chosen payment: pay it (or choose Not now);
 * - `outdated` — the page predates paying before registering: reload it;
 * - `refused`  — the provider would not start the payment.
 */
export type CheckoutRefusalKind =
  | 'retry'
  | 'reprice'
  | 'locked'
  | 'unpaid'
  | 'outdated'
  | 'refused'

const KINDS: Readonly<Record<string, CheckoutRefusalKind>> = {
  payment_update_in_progress: 'retry',
  payment_retry: 'retry',
  payment_quote_changed: 'reprice',
  payment_locked: 'locked',
  payment_in_progress: 'locked',
  payment_unresolved: 'locked',
  payment_required: 'unpaid',
  checkout_outdated: 'outdated',
  payment_refused: 'refused',
}

export interface CheckoutRefusal {
  kind: CheckoutRefusalKind
  code: string
  message: string | null
}

/** The checkout refusal in an error, or null when it is not one (a field error, a network failure…). */
export const classifyCheckoutRefusal = (
  error: unknown
): CheckoutRefusal | null => {
  const apiError = asApiError(error)
  const code =
    typeof apiError?.error_code === 'string' ? apiError.error_code : null

  if (code === null || KINDS[code] === undefined) {
    return null
  }

  return {
    kind: KINDS[code],
    code,
    message:
      typeof apiError?.message === 'string' && apiError.message !== ''
        ? apiError.message
        : null,
  }
}
